import React from "react";
import { Box, Chip, Paper, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";

/** The fields a package card needs — shared by the owner and superadmin views. */
export interface PackageCardData {
  name: string;
  description: string | null;
  price: number;
  currency: string;
  durationDays: number;
  features: string[];
  isActive: boolean;
}

export const durationLabel = (days: number) => {
  if (days % 365 === 0) return days === 365 ? "year" : `${days / 365} years`;
  if (days % 30 === 0) return days === 30 ? "month" : `${days / 30} months`;
  return `${days} days`;
};

/**
 * One package as a tenant owner sees it on the "subscription ended" screen.
 * The superadmin Packages page renders the same card, so what they edit is
 * exactly what owners get.
 */
const PackageCard: React.FC<{
  pkg: PackageCardData;
  highlighted?: boolean;
  footer?: React.ReactNode;
}> = ({ pkg, highlighted, footer }) => (
  <Paper
    elevation={0}
    sx={{
      border: "1px solid",
      borderColor: highlighted ? "primary.main" : "divider",
      boxShadow: highlighted ? "0 0 0 1px currentColor" : "none",
      color: "text.primary",
      borderRadius: 3,
      p: 2.5,
      display: "flex",
      flexDirection: "column",
      gap: 1.25,
      opacity: pkg.isActive ? 1 : 0.55,
      height: "100%",
      bgcolor: "background.paper",
    }}
  >
    <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
      <Typography sx={{ fontWeight: 700, fontSize: "1.05rem" }}>{pkg.name}</Typography>
      {!pkg.isActive && (
        <Chip size="small" label="Archived" sx={{ fontWeight: 600, height: 22 }} />
      )}
    </Box>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, flexWrap: "wrap" }}>
      <Typography sx={{ fontWeight: 800, fontSize: "1.9rem", lineHeight: 1 }}>
        {pkg.price.toLocaleString()}
      </Typography>
      <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
        {pkg.currency} / {durationLabel(pkg.durationDays)}
      </Typography>
    </Box>
    {pkg.description && (
      <Typography sx={{ fontSize: "0.9rem", color: "text.secondary" }}>
        {pkg.description}
      </Typography>
    )}
    {pkg.features.length > 0 && (
      <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
        {pkg.features.map((feature) => (
          <Box
            component="li"
            key={feature}
            sx={{ display: "flex", gap: 1, alignItems: "flex-start", py: 0.4 }}
          >
            <CheckIcon sx={{ fontSize: 18, color: "#17663F", mt: "2px" }} aria-hidden />
            <Typography sx={{ fontSize: "0.9rem" }}>{feature}</Typography>
          </Box>
        ))}
      </Box>
    )}
    <Box sx={{ flexGrow: 1 }} />
    {footer}
  </Paper>
);

export default PackageCard;
